// Parts.js
import React, { useState, useRef, useEffect, useMemo } from 'react';
import { FaFolder, FaFolderPlus, FaEdit, FaTrash } from 'react-icons/fa';
import { BsThreeDotsVertical } from 'react-icons/bs';
import './Parts.css';
import { getAPIBaseURL } from '../../../utils/api';
import { apiGet } from '../../../utils/apiClient';
const API_BASE_URL = getAPIBaseURL();

const Parts = ({
  parts,
  onSelectPart,
  selectedPart,
  onPartUpdated = () => {},
  onDocumentsUpdated = () => {},
}) => {
  const [openMenuId, setOpenMenuId] = useState(null);
  const [docCounts, setDocCounts] = useState({});
  const [confirmDeletePart, setConfirmDeletePart] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [addPartName, setAddPartName] = useState('');
  const [renameModal, setRenameModal] = useState(null);
  const [renamePartName, setRenamePartName] = useState('');
  const menuRef = useRef(null);

  // fetch document counts per part
  const fetchDocCounts = () => {
    apiGet('/api/parts/document-counts')
      .then((data) => setDocCounts(data || {}))
      .catch(() => {});
  };

  useEffect(() => { fetchDocCounts(); }, []);
  // refresh counts when parts change (add/rename/delete triggers onPartUpdated → parts re-fetch)
  useEffect(() => { fetchDocCounts(); }, [parts]);

  /* ---------- HELPERS ---------- */

  // close dropdown on outside click
  useEffect(() => {
    function handleClickOutside(e) {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setOpenMenuId(null);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // show only children of the currently selected part
  const isChildPart = (part) => {
    const selectedPartStr = String(selectedPart);
    const partParentIdStr = String(part.parent_id);

    return selectedPartStr === '1'
      ? partParentIdStr === '1'
      : partParentIdStr === selectedPartStr;
  };

  const filteredParts = parts.filter(
    (part) => part.id !== '1' && isChildPart(part),
  );

  const childCountMap = useMemo(() => {
    const map = {};
    parts.forEach((part) => {
      const parentKey = String(part.parent_id);
      map[parentKey] = (map[parentKey] || 0) + 1;
    });
    return map;
  }, [parts]);

  const toggleMenu = (e, id) => {
    e.stopPropagation();
    setOpenMenuId((prev) => (prev === id ? null : id));
  };

  /* ---------- API ACTIONS ---------- */

  // add a new part under the currently selected node
  const addPart = () => {
    setAddPartName('');
    setShowAddModal(true);
  };

  const handleConfirmAddPart = async () => {
    const name = addPartName.trim();
    if (!name) return;
    setShowAddModal(false);
    const payload = { name, parent_id: selectedPart || '1' };
    try {
      const response = await fetch(`${API_BASE_URL}/api/parts/add`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
        credentials: 'include',
      });
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Fehler beim Hinzufügen der Komponente.');
      }
      onPartUpdated();
      await new Promise((r) => setTimeout(r, 250));
      onDocumentsUpdated();
    } catch (err) {
      console.error(err);
    }
  };

  const renamePart = (part) => {
    setRenamePartName(part.name);
    setRenameModal(part);
  };

  const handleConfirmRenamePart = async () => {
    const part = renameModal;
    const newName = renamePartName.trim();
    setRenameModal(null);
    if (!newName || newName === part.name) return;
    try {
      const response = await fetch(`${API_BASE_URL}/api/parts/rename/${part.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: newName }),
        credentials: 'include',
      });
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Fehler beim Umbenennen der Komponente.');
      }
      onPartUpdated();
      await new Promise((r) => setTimeout(r, 250));
      onDocumentsUpdated();
    } catch (err) {
      console.error(err);
    }
  };

  const deletePart = (part) => {
    setConfirmDeletePart(part);
  };

  const handleConfirmDeletePart = async () => {
    const part = confirmDeletePart;
    setConfirmDeletePart(null);
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/parts/delete/${part.id}`,
        { method: 'DELETE', credentials: 'include' },
      );
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Fehler beim Löschen der Komponente.');
      }
      onPartUpdated(part.id);
      onDocumentsUpdated();
    } catch (err) {
      console.error(err);
    }
  };

  /* ---------- MENU HANDLERS ---------- */

  const handleRenameClick = (e, part) => {
    e.stopPropagation();
    setOpenMenuId(null);
    renamePart(part);
  };

  const handleDeleteClick = (e, part) => {
    e.stopPropagation();
    setOpenMenuId(null);
    deletePart(part);
  };

  /* ---------- RENDER ---------- */

  return (
    <>
        <div className="parts-grid">
            {filteredParts.length > 0 ? (
              filteredParts.map((part) => {
                const childCountKey = String(part.id);
                const childCount = childCountMap[childCountKey] || 0;
                const hasChildren = childCount > 0;
                const childMetaText = hasChildren
                  ? `Unterkomponente${childCount === 1 ? '' : 'n'}`
                  : 'Noch keine Unterkomponenten';
                const footerHint = hasChildren
                  ? 'Unterkomponenten vorhanden'
                  : 'Bereit für erste Unterkomponente';
                const menuId = `part-menu-${part.id}`;

                return (
                  <div
                    key={part.id}
                    className={`part-tile ${selectedPart === part.id ? 'selected' : ''}`}
                    onClick={() => onSelectPart(part.id)}
                    tabIndex={0}
                    role="button"
                    aria-label={`Komponente ${part.name}`}
                  >
                    <div className="part-header">
                      <div className="part-heading">
                        <div className="part-icon-wrapper" aria-hidden="true">
                          <FaFolder className="folder-icon" />
                        </div>
                        <h3 className="part-name" title={part.name}>
                          {part.name}
                        </h3>
                      </div>
                      <button
                        type="button"
                        className="part-menu-trigger"
                        onClick={(e) => toggleMenu(e, part.id)}
                        aria-label="Menü öffnen"
                        aria-haspopup="menu"
                        aria-controls={menuId}
                        aria-expanded={openMenuId === part.id}
                      >
                        <BsThreeDotsVertical />
                      </button>
                    </div>

                    <div className="part-footer">
                      <span className="part-count-text">
                        {childCount === 1 ? '1 Unterkomponente' : `${childCount} Unterkomponenten`}
                      </span>
                      <span className="part-count-text">
                        {(docCounts[String(part.id)] || 0) === 1
                          ? '1 Dokument'
                          : `${docCounts[String(part.id)] || 0} Dokumente`}
                      </span>
                    </div>

                    {openMenuId === part.id && (
                      <ul
                        id={menuId}
                        className="part-menu"
                        ref={menuRef}
                        onClick={(e) => e.stopPropagation()}
                        role="menu"
                      >
                        <li onClick={(e) => handleRenameClick(e, part)} role="menuitem">
                          <FaEdit className="menu-item-icon" />
                          Umbenennen
                        </li>
                        <li
                          className="delete"
                          onClick={(e) => handleDeleteClick(e, part)}
                          role="menuitem"
                        >
                          <FaTrash className="menu-item-icon" />
                          Entfernen
                        </li>
                      </ul>
                    )}
                  </div>
                );
              })
            ) : (
            <div className="no-parts-message">Keine Komponenten vorhanden</div>
          )}
          <button
            className="part-tile add-part-chip"
            onClick={addPart}
            aria-label="Komponente hinzufügen"
            title="Neue Komponente"
          >
            <FaFolderPlus style={{ fontSize: '0.6875rem', color: '#94a3b8' }} />
            <span className="part-name" style={{ color: '#94a3b8' }}>+</span>
          </button>
        </div>
      {confirmDeletePart && (
        <div className="confirm-modal-overlay" onClick={() => setConfirmDeletePart(null)}>
          <div className="confirm-modal" onClick={(e) => e.stopPropagation()}>
            <p>Möchten Sie <strong>"{confirmDeletePart.name}"</strong> wirklich entfernen?</p>
            <p className="confirm-modal-warning">Alle Unterkomponenten und verbundenen Dokumente werden ebenfalls dauerhaft gelöscht.</p>
            <div className="confirm-modal-actions">
              <button className="confirm-modal-cancel" onClick={() => setConfirmDeletePart(null)}>Abbrechen</button>
              <button className="confirm-modal-delete" onClick={handleConfirmDeletePart}>Entfernen</button>
            </div>
          </div>
        </div>
      )}
      {showAddModal && (
        <div className="confirm-modal-overlay" onClick={() => setShowAddModal(false)}>
          <div className="confirm-modal" onClick={(e) => e.stopPropagation()}>
            <p><strong>Neue Komponente hinzufügen</strong></p>
            <input
              className="confirm-modal-input"
              type="text"
              placeholder="Name der Komponente"
              value={addPartName}
              onChange={(e) => setAddPartName(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleConfirmAddPart()}
              autoFocus
            />
            <div className="confirm-modal-actions">
              <button className="confirm-modal-cancel" onClick={() => setShowAddModal(false)}>Abbrechen</button>
              <button className="confirm-modal-confirm" onClick={handleConfirmAddPart} disabled={!addPartName.trim()}>Hinzufügen</button>
            </div>
          </div>
        </div>
      )}
      {renameModal && (
        <div className="confirm-modal-overlay" onClick={() => setRenameModal(null)}>
          <div className="confirm-modal" onClick={(e) => e.stopPropagation()}>
            <p>Umbenennen: <strong>"{renameModal.name}"</strong></p>
            <input
              className="confirm-modal-input"
              type="text"
              value={renamePartName}
              onChange={(e) => setRenamePartName(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleConfirmRenamePart()}
              autoFocus
            />
            <div className="confirm-modal-actions">
              <button className="confirm-modal-cancel" onClick={() => setRenameModal(null)}>Abbrechen</button>
              <button className="confirm-modal-confirm" onClick={handleConfirmRenamePart} disabled={!renamePartName.trim()}>Umbenennen</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Parts;
